import type { ModuleContext } from '@gzl10/nexus-sdk'
import cron from 'node-cron'
import type { ScheduledTask } from 'node-cron'
import { CronExpressionParser } from 'cron-parser'
import type {
  Schedule,
  ScheduleJob,
  RetryBackoff,
  CreateScheduleInput,
  UpdateScheduleInput,
  ScheduleFunction,
  ExecutionResult,
  NextRunInfo
} from './schedules.types.js'
import {
  executeTarget,
  registerFunction as registerFn,
  unregisterFunction as unregisterFn,
  listRegisteredFunctions,
  hasFunctionRegistered
} from './schedules.executor.js'

/** Minimal interface for runtime entity services accessed via ctx.services */
interface RuntimeEntityService<T = unknown> {
  findAll(query?: Record<string, unknown>): Promise<{ items: T[] }>
  findById(id: string): Promise<T | null>
  create?(data: Partial<T>): Promise<T>
  update?(id: string, data: Partial<T>): Promise<T>
  delete?(id: string): Promise<void>
}

/**
 * Schedules business-logic service (cron jobs, retries, execution).
 *
 * CRUD is delegated to the runtime CollectionService registered as 'schedules'.
 * Direct Knex is used only for narrow internal state mutations (atomic lock,
 * status transitions during execution) that must not trigger entity hooks.
 */
export class SchedulesService {
  private ctx: ModuleContext
  private jobs: Map<string, ScheduleJob> = new Map()
  private retryTimeouts: Set<ReturnType<typeof setTimeout>> = new Set()
  private initialized = false
  private initPromise: Promise<void> | null = null
  private logger: ModuleContext['core']['logger']

  constructor(ctx: ModuleContext) {
    this.ctx = ctx
    this.logger = ctx.core.logger.child({ service: 'schedules' })
  }

  /** Runtime CollectionService for 'schedules' entity */
  private get entityService(): RuntimeEntityService<Schedule> {
    return this.ctx.services.get<RuntimeEntityService<Schedule>>('schedules')
  }

  /** Runtime CollectionService for 'schedule_logs' entity */
  private get logsService(): RuntimeEntityService<Record<string, unknown>> {
    return this.ctx.services.get<RuntimeEntityService<Record<string, unknown>>>('schedule_logs')
  }

  /** Format a Date for the current DB dialect (MySQL needs 'YYYY-MM-DD HH:MM:SS') */
  private ts(date?: Date): string {
    return this.ctx.db.formatTimestamp(this.ctx.db.knex, date ?? new Date())
  }

  // ── Lifecycle ────────────────────────────────────────────────────────

  /**
   * Initializes the service by loading active schedules from the DB.
   * Safe to call multiple times - concurrent calls share the same initialization.
   */
  async init(): Promise<void> {
    if (this.initialized) return
    if (this.initPromise) return this.initPromise

    this.initPromise = this.doInit()
    return this.initPromise
  }

  private async doInit(): Promise<void> {
    this.ctx.core.logger.debug('Initializing schedules service...')

    // Bulk load: direct Knex with specific WHERE clause (not available via EntityService.findAll)
    const rows = await this.ctx.db.knex<Schedule>('schedules')
      .where('enabled', true)
      .whereNot('status', 'paused')
      .select('*')

    // Parse JSON fields (Knex returns raw strings on SQLite/MySQL)
    const schedules = rows.map(s => ({
      ...s,
      function_args: typeof s.function_args === 'string' ? JSON.parse(s.function_args) as Record<string, unknown> : s.function_args,
    }))

    for (const schedule of schedules) {
      this.scheduleJob(schedule)
    }

    this.initialized = true
    this.ctx.core.logger.info(`Schedules service initialized with ${schedules.length} active jobs`)
  }

  shutdown(): void {
    this.ctx.core.logger.debug('Shutting down schedules service...')

    for (const timeout of this.retryTimeouts) {
      clearTimeout(timeout)
    }
    this.retryTimeouts.clear()

    for (const [id, job] of this.jobs) {
      job.task.stop()
      this.ctx.core.logger.debug(`Stopped job: ${id}`)
    }

    this.jobs.clear()
    this.initialized = false
    this.initPromise = null
    this.ctx.core.logger.info('Schedules service shut down')
  }

  // ── CRUD (delegated to runtime CollectionService) ────────────────────

  /**
   * Creates a new schedule via the runtime entity service.
   * The afterCreate hook (in schedules.entity.ts) calls syncJob() automatically.
   */
  async create(data: CreateScheduleInput, userId?: string): Promise<Schedule> {
    if (!cron.validate(data.cron_expression)) {
      throw new this.ctx.core.errors.ValidationError(`Invalid cron expression: ${data.cron_expression}`)
    }

    const createData: Record<string, unknown> = {
      name: data.name,
      description: data.description ?? null,
      cron_expression: data.cron_expression,
      timezone: data.timezone ?? 'UTC',
      function_key: data.function_key,
      function_args: data.function_args ?? null,
      enabled: data.enabled ?? true,
      max_retries: data.max_retries ?? 0,
      retry_delay_ms: data.retry_delay_ms ?? 5000,
      retry_backoff: data.retry_backoff ?? 'exponential',
    }
    if (userId) createData['_authUserId'] = userId

    const created = await this.entityService.create!(createData as Partial<Schedule>)
    return created
  }

  /**
   * Updates an existing schedule via the runtime entity service.
   * The afterUpdate hook calls syncJob() automatically.
   */
  async update(id: string, data: UpdateScheduleInput, userId?: string): Promise<Schedule> {
    if (data.cron_expression && !cron.validate(data.cron_expression)) {
      throw new this.ctx.core.errors.ValidationError(`Invalid cron expression: ${data.cron_expression}`)
    }

    const updateData: Record<string, unknown> = { ...data }
    if (userId) updateData['_authUserId'] = userId

    const updated = await this.entityService.update!(id, updateData as Partial<Schedule>)
    return updated
  }

  /**
   * Deletes a schedule via the runtime entity service.
   * The afterDelete hook calls cancelJobById() automatically.
   */
  async delete(id: string): Promise<void> {
    await this.entityService.delete!(id)
  }

  /**
   * Finds a schedule by ID via the runtime entity service.
   * JSON fields (function_args) are parsed automatically by the runtime.
   */
  async findById(id: string): Promise<Schedule | null> {
    return this.entityService.findById(id)
  }

  /**
   * Returns all schedules via the runtime entity service.
   */
  async findAll(): Promise<Schedule[]> {
    return (await this.entityService.findAll()).items
  }

  // ── Business logic ───────────────────────────────────────────────────

  async pause(id: string): Promise<void> {
    const schedule = await this.findById(id)
    if (!schedule) {
      throw new this.ctx.core.errors.NotFoundError(`Schedule not found: ${id}`)
    }

    this.cancelJob(id)

    // Direct Knex: internal state mutation (avoid triggering afterUpdate → syncJob loop)
    await this.ctx.db.knex('schedules')
      .where('id', id)
      .update({ status: 'paused', updated_at: this.ts() })

    this.ctx.core.logger.info(`Schedule paused: ${schedule.name} (${id})`)
  }

  async resume(id: string): Promise<void> {
    const schedule = await this.findById(id)
    if (!schedule) {
      throw new this.ctx.core.errors.NotFoundError(`Schedule not found: ${id}`)
    }

    if (!schedule.enabled) {
      throw new this.ctx.core.errors.ValidationError(`Cannot resume disabled schedule: ${id}`)
    }

    // Direct Knex: internal state mutation
    await this.ctx.db.knex('schedules')
      .where('id', id)
      .update({ status: 'idle', updated_at: this.ts() })

    const updated = await this.findById(id)
    if (updated) {
      this.scheduleJob(updated)
    }

    this.ctx.core.logger.info(`Schedule resumed: ${schedule.name} (${id})`)
  }

  async trigger(id: string): Promise<ExecutionResult> {
    const schedule = await this.findById(id)
    if (!schedule) {
      throw new this.ctx.core.errors.NotFoundError(`Schedule not found: ${id}`)
    }

    this.ctx.core.logger.info(`Manual trigger: ${schedule.name} (${id})`)
    return this.executeSchedule(schedule)
  }

  async getNextRuns(limit = 10): Promise<NextRunInfo[]> {
    // Direct Knex: specific columns + WHERE clause not available via EntityService
    const schedules = await this.ctx.db.knex<Schedule>('schedules')
      .where('enabled', true)
      .whereNot('status', 'paused')
      .select('id', 'name', 'cron_expression', 'timezone')
      .limit(limit)

    const result: NextRunInfo[] = []

    for (const schedule of schedules) {
      const nextDate = this.getNextRunDate(schedule.cron_expression, schedule.timezone)
      if (nextDate) {
        result.push({
          id: schedule.id,
          name: schedule.name,
          cron_expression: schedule.cron_expression,
          next_run: nextDate.toISOString()
        })
      }
    }

    return result.sort((a, b) => new Date(a.next_run).getTime() - new Date(b.next_run).getTime())
  }

  /**
   * Syncs a job with the DB (called from entity hooks after CRUD via API/UI)
   */
  async syncJob(id: string): Promise<void> {
    const schedule = await this.findById(id)
    if (!schedule) {
      this.cancelJob(id)
      return
    }

    this.cancelJob(id)

    if (schedule.enabled && schedule.status !== 'paused') {
      this.scheduleJob(schedule)
    }
  }

  cancelJobById(id: string): void {
    this.cancelJob(id)
  }

  registerFunction(key: string, fn: ScheduleFunction): void {
    registerFn(key, fn)
    this.ctx.core.logger.debug(`Function registered: ${key}`)
  }

  unregisterFunction(key: string): void {
    unregisterFn(key)
    this.ctx.core.logger.debug(`Function unregistered: ${key}`)
  }

  getRegisteredFunctions(): string[] {
    return listRegisteredFunctions()
  }

  /**
   * Ensures a system schedule exists (idempotent). Creates it if not found,
   * or updates function_key if it changed (handles historical renames).
   */
  async ensureSystemSchedule(data: Omit<CreateScheduleInput, 'enabled'>): Promise<void> {
    // Direct Knex for existence check (lightweight, no JSON parsing needed)
    const existing = await this.ctx.db.knex('schedules')
      .where('name', data.name)
      .first()
    if (existing) {
      if (existing.function_key !== data.function_key) {
        await this.ctx.db.knex('schedules')
          .where('name', data.name)
          .update({ function_key: data.function_key })
        this.logger.info(`System schedule function_key updated: ${data.name} → ${data.function_key}`)
      }
      return
    }
    await this.create({ ...data, enabled: true })
    this.logger.info(`System schedule created: ${data.name}`)
  }

  /**
   * Logs warnings for active schedules that reference unregistered functions
   */
  async checkOrphanFunctions(): Promise<void> {
    // Direct Knex: lightweight check, only needs 3 columns
    const active = await this.ctx.db.knex<Schedule>('schedules')
      .where('enabled', true)
      .whereNot('status', 'paused')
      .select('id', 'name', 'function_key')

    for (const schedule of active) {
      if (!hasFunctionRegistered(schedule.function_key)) {
        this.logger.warn(`Schedule "${schedule.name}" references unregistered function "${schedule.function_key}"`)
      }
    }
  }

  // ── Private methods ──────────────────────────────────────────────────

  private scheduleJob(schedule: Schedule): void {
    if (this.jobs.has(schedule.id)) {
      this.ctx.core.logger.warn(`Job already scheduled: ${schedule.id}`)
      return
    }

    try {
      const task: ScheduledTask = cron.schedule(
        schedule.cron_expression,
        async () => {
          await this.executeSchedule(schedule)
        },
        {
          timezone: schedule.timezone
        }
      )

      this.jobs.set(schedule.id, { id: schedule.id, task, schedule })
      this.logger.debug(`Job scheduled: ${schedule.name} (${schedule.cron_expression})`)
    } catch (err) {
      this.logger.error({ scheduleId: schedule.id, cron: schedule.cron_expression, err },
        `Failed to schedule job "${schedule.name}" — invalid cron expression?`)
    }
  }

  private cancelJob(id: string): void {
    const job = this.jobs.get(id)
    if (job) {
      job.task.stop()
      this.jobs.delete(id)
      this.logger.info(`Job cancelled: ${id}`)
    }
  }

  /**
   * Executes a schedule with retry support.
   * All state mutations here use direct Knex intentionally:
   * - Atomic lock (status != running) must be a single UPDATE
   * - Status/counter updates are internal and must not trigger entity hooks
   */
  private async executeSchedule(schedule: Schedule): Promise<ExecutionResult> {
    // Atomic lock: UPDATE only if not already running (cluster-safe)
    const claimed = await this.ctx.db.knex('schedules')
      .where('id', schedule.id)
      .where('status', '!=', 'running')
      .update({ status: 'running' })

    if (!claimed || claimed === 0) {
      this.logger.warn(`Skipping execution, already running: ${schedule.name}`)
      await this.logExecution(schedule.id, {
        success: false,
        error: 'Skipped: previous execution still running',
        duration_ms: 0
      }, 'skipped')
      return { success: false, error: 'Skipped: already running', duration_ms: 0 }
    }

    // Re-fetch fresh state after claiming lock
    const current = await this.findById(schedule.id)

    // Ejecutar
    const result = await executeTarget(current ?? schedule)

    if (result.success) {
      await this.ctx.db.knex('schedules')
        .where('id', schedule.id)
        .update({
          status: 'idle',
          last_run_at: this.ts(),
          last_error: null,
          run_count: this.ctx.db.knex.raw('run_count + 1'),
          retry_count: 0
        })
      await this.logExecution(schedule.id, result, 'success')
      this.logger.info(`Schedule executed: ${schedule.name} (${result.duration_ms}ms)`)
      return result
    }

    // --- RETRY LOGIC ---
    const maxRetries = schedule.max_retries ?? 0
    const currentRetryCount = current?.retry_count ?? 0

    if (maxRetries > 0 && currentRetryCount < maxRetries) {
      const newRetryCount = currentRetryCount + 1
      const delay = this.calculateRetryDelay(schedule, newRetryCount)

      await this.ctx.db.knex('schedules')
        .where('id', schedule.id)
        .update({
          status: 'retrying',
          retry_count: newRetryCount,
          last_error: result.error ?? null,
          last_run_at: this.ts(),
          run_count: this.ctx.db.knex.raw('run_count + 1')
        })
      await this.logExecution(schedule.id, result, 'retrying')
      this.logger.warn(`Schedule retry ${newRetryCount}/${maxRetries}: ${schedule.name} in ${delay}ms`)

      const timeoutId = setTimeout(async () => {
        this.retryTimeouts.delete(timeoutId)
        const fresh = await this.findById(schedule.id)
        if (fresh && fresh.status === 'retrying' && fresh.enabled) {
          await this.executeSchedule(fresh)
        }
      }, delay)
      this.retryTimeouts.add(timeoutId)

      return result
    }

    // No retries or retries exhausted
    const logStatus = maxRetries > 0 ? 'exhausted' : 'error'
    await this.ctx.db.knex('schedules')
      .where('id', schedule.id)
      .update({
        status: 'error',
        last_run_at: this.ts(),
        last_error: result.error ?? null,
        run_count: this.ctx.db.knex.raw('run_count + 1'),
        retry_count: 0
      })
    await this.logExecution(schedule.id, result, logStatus)

    if (logStatus === 'exhausted') {
      this.logger.error(`Schedule exhausted after ${maxRetries} retries: ${schedule.name} - ${result.error}`)
    } else {
      this.logger.error(`Schedule failed: ${schedule.name} - ${result.error}`)
    }

    return result
  }

  private calculateRetryDelay(schedule: Schedule, attempt: number): number {
    const baseDelay = schedule.retry_delay_ms ?? 5000
    const backoff: RetryBackoff = schedule.retry_backoff ?? 'exponential'

    switch (backoff) {
      case 'fixed':
        return baseDelay
      case 'linear':
        return baseDelay * attempt
      case 'exponential':
        return baseDelay * Math.pow(2, attempt - 1)
      default:
        return baseDelay
    }
  }

  /**
   * Records an execution log via the runtime entity service for schedule_logs.
   */
  private async logExecution(
    scheduleId: string,
    result: ExecutionResult,
    status: 'success' | 'error' | 'skipped' | 'retrying' | 'exhausted'
  ): Promise<void> {
    const now = new Date()
    const startedAt = new Date(now.getTime() - result.duration_ms)

    await this.logsService.create!({
      schedule_id: scheduleId,
      started_at: this.ts(startedAt),
      finished_at: this.ts(now),
      duration_ms: result.duration_ms,
      status,
      result: result.data ?? null,
      error: result.error ?? null,
    } as Record<string, unknown>)
  }

  private getNextRunDate(cronExpression: string, timezone: string): Date | null {
    try {
      const interval = CronExpressionParser.parse(cronExpression, {
        tz: timezone
      })
      return interval.next().toDate()
    } catch {
      return null
    }
  }
}
